import { useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';

interface KeyboardShortcut {
  key: string;
  modifiers: ('ctrl' | 'meta' | 'shift' | 'alt')[];
  action: () => void;
  description: string;
  showToast?: boolean;
  toastMessage?: string;
}

interface UseKeyboardShortcutsOptions {
  enabled?: boolean;
  shortcuts?: KeyboardShortcut[];
}

// Default shortcuts that work globally
const defaultShortcuts: Omit<KeyboardShortcut, 'action'>[] = [
  { key: 'Enter', modifiers: ['meta'], description: 'Generate content' },
  { key: 'Enter', modifiers: ['ctrl'], description: 'Generate content' },
  { key: 'Escape', modifiers: [], description: 'Close modal/dialog' },
];

export function useKeyboardShortcuts(options: UseKeyboardShortcutsOptions = {}) {
  const { enabled = true, shortcuts = [] } = options;
  const { toast } = useToast();

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Skip if user is typing in an input field (unless it's Escape)
    const target = e.target as HTMLElement;
    const isInputField = target.tagName === 'INPUT' || 
                         target.tagName === 'TEXTAREA' || 
                         target.isContentEditable;
    
    if (isInputField && e.key !== 'Escape') {
      return;
    }

    // Check custom shortcuts first
    for (const shortcut of shortcuts) {
      const hasCtrl = shortcut.modifiers.includes('ctrl');
      const hasMeta = shortcut.modifiers.includes('meta');
      const hasShift = shortcut.modifiers.includes('shift');
      const hasAlt = shortcut.modifiers.includes('alt');

      const ctrlMatch = hasCtrl ? e.ctrlKey : (hasMeta ? false : !e.ctrlKey);
      const metaMatch = hasMeta ? e.metaKey : (hasCtrl ? false : !e.metaKey);
      const shiftMatch = hasShift === e.shiftKey;
      const altMatch = hasAlt === e.altKey;
      const keyMatch = e.key.toLowerCase() === shortcut.key.toLowerCase();

      // For Cmd/Ctrl shortcuts, either modifier works
      const modifierMatch = (hasCtrl || hasMeta) 
        ? (e.ctrlKey || e.metaKey) && shiftMatch && altMatch
        : ctrlMatch && metaMatch && shiftMatch && altMatch;

      if (keyMatch && modifierMatch) {
        e.preventDefault();
        shortcut.action();
        
        if (shortcut.showToast && shortcut.toastMessage) {
          toast({
            title: shortcut.toastMessage,
            duration: 2000,
          });
        }
        return;
      }
    }

    // Handle Cmd/Ctrl + Enter for generate buttons
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      const generateButton = document.querySelector('[data-shortcut="generate"]') as HTMLButtonElement;
      if (generateButton && !generateButton.disabled) {
        e.preventDefault();
        generateButton.click();
        toast({
          title: 'Generating content...',
          description: 'Shortcut: ⌘+Enter',
          duration: 2000,
        });
      }
    }

    // Handle Escape for closing modals
    if (e.key === 'Escape') {
      // Dispatch a custom event that modals can listen for
      window.dispatchEvent(new CustomEvent('keyboard-escape'));
    }
  }, [shortcuts, toast]);

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, handleKeyDown]);
}

// Utility to get the correct modifier key symbol based on OS
export function getModifierSymbol(): '⌘' | 'Ctrl' {
  if (typeof navigator !== 'undefined') {
    const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);
    return isMac ? '⌘' : 'Ctrl';
  }
  return '⌘';
}

// List of available keyboard shortcuts for Settings page
export const keyboardShortcutsList = [
  { keys: ['⌘/Ctrl', 'Enter'], description: 'Generate content (when on generator pages)' },
  { keys: ['Escape'], description: 'Close modal or dialog' },
  { keys: ['⌘/Ctrl', 'C'], description: 'Copy selected text' },
] as const;
